/**
 * @fileoverview Hook for managing character state and operations.
 * @module @hooks/useCharacter
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { characterDb, toCharacterListItem } from '../db/CharacterDatabase';
import { characterSnapshotService } from '../services/CharacterSnapshotService';
import type {
  Character,
  CreateCharacterInput,
  UpdateCharacterInput,
  CharacterVaultSettings,
  CharacterListItem,
} from '../db/characterTypes';
import {
  dropOpenLorebookPayload,
  registerCharacterPayloadDrop,
} from '../utils/workspaceExclusive';
import { discardPendingSavesForCharacter } from '../utils/characterPendingSaveDiscard';
import { flushChatSessions } from '../utils/chatSessionFlush';
import {
  dropPendingChatWritesForOwner,
  pendingChatWritesForOwner,
} from '../services/ChatHistoryService';

const IMPORTED_CHARACTER_FLAG = 'character_vault_imported';

/**
 * Result type for character operations
 */
interface CharacterResult {
  characters: Character[];
  characterListItems: CharacterListItem[];
  currentCharacter: Character | null;
  isLoading: boolean;
  error: Error | null;
  settings: CharacterVaultSettings | null;
}

/**
 * Operations type for character actions
 */
interface CharacterOperations {
  createCharacter: (input: CreateCharacterInput) => Promise<Character>;
  openCharacter: (characterId: string) => Promise<void>;
  closeCharacter: () => void;
  deleteCharacter: (characterId: string) => Promise<void>;
  updateCharacter: (characterId: string, input: UpdateCharacterInput) => Promise<Character>;
  duplicateCharacter: (characterId: string, newName: string) => Promise<Character>;
  refreshCharacters: () => Promise<void>;
  updateSpecField: (characterId: string, field: keyof Character['data']['spec'], value: string | string[]) => Promise<Character>;
  updateSettings: (updates: Partial<Omit<CharacterVaultSettings, 'id'>>) => Promise<void>;
  /** Re-read app settings from IndexedDB into context (e.g. after settings panel save). */
  refreshSettings: () => Promise<void>;
}

/**
 * Hook for managing character state and operations
 * @returns {[CharacterResult, CharacterOperations]} Character state and operations
 */
export function useCharacter(): [CharacterResult, CharacterOperations] {
  const [characters, setCharacters] = useState<Character[]>([]);
  const [characterListItems, setCharacterListItems] = useState<CharacterListItem[]>([]);
  const [currentCharacterId, setCurrentCharacterId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [settings, setSettings] = useState<CharacterVaultSettings | null>(null);
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const specUpdateSequenceRef = useRef<Map<string, number>>(new Map());
  const currentCharacter = useMemo(
    () => characters.find(character => character.id === currentCharacterId) ?? null,
    [characters, currentCharacterId],
  );

  /** Clear open card from memory (used by lorebook open and local close). */
  const dropCharacterPayload = useCallback(() => {
    setCurrentCharacterId(null);
    setCharacters([]);
    // Sequence entries guard stale writes for the open card only; drop them
    // with the card so the map does not grow per opened character × field.
    specUpdateSequenceRef.current.clear();
    const prefs = settingsRef.current;
    if (prefs?.lastActiveCharacterId !== undefined) {
      void characterDb.updateSettings({ lastActiveCharacterId: undefined });
      setSettings({ ...prefs, lastActiveCharacterId: undefined });
    }
  }, []);

  useEffect(() => registerCharacterPayloadDrop(dropCharacterPayload), [dropCharacterPayload]);

  /**
   * Load list items and settings on mount
   * Full Character objects are loaded only when needed
   */
  useEffect(() => {
    async function loadData() {
      try {
        setIsLoading(true);
        const [items, prefs] = await Promise.all([
          characterDb.getAllCharacterListItems(),
          characterDb.getSettings(),
        ]);
        setCharacterListItems(items);
        setSettings(prefs);
        // Don't restore last active character - always start at character selection
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load characters'));
      } finally {
        setIsLoading(false);
      }
    }
    loadData();
  }, []);

  /**
   * Refresh characters list (list items only)
   */
  const refreshCharacters = useCallback(async () => {
    try {
      const items = await characterDb.getAllCharacterListItems();
      setCharacterListItems(items);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to refresh characters'));
    }
  }, []);

  /**
   * Create a new character
   */
  const createCharacter = useCallback(async (input: CreateCharacterInput): Promise<Character> => {
    // Exclusive workspace: never hold a full vault lorebook while a card is open
    dropOpenLorebookPayload();
    const created = await characterDb.createCharacter(input);
    // Creating here also opens the card, so record the open like openCharacter does.
    // The card is already saved; a failed timestamp write must not report the create as failed.
    const lastOpenedAt = await characterDb.updateLastOpened(created.id).catch(() => undefined);
    const character = { ...created, lastOpenedAt };
    // Only keep the open card in memory - never stack full characters
    setCharacters([character]);
    setCharacterListItems((prev) => [toCharacterListItem(character), ...prev]);
    setCurrentCharacterId(character.id);

    // Update last active character in settings
    if (settings) {
      await characterDb.updateSettings({ lastActiveCharacterId: character.id });
      setSettings({ ...settings, lastActiveCharacterId: character.id });
    }

    return character;
  }, [settings]);

  /**
   * Open a character for editing
   */
  const openCharacter = useCallback(async (characterId: string): Promise<void> => {
    try {
      // Exclusive workspace: drop full lorebook before loading a full card
      dropOpenLorebookPayload();
      const character = await characterDb.getCharacter(characterId);
      if (character) {
        // Replace - do not accumulate every previously opened card in heap
        setCharacters([character]);

        const lastOpenedAt = await characterDb.updateLastOpened(characterId);
        // Patch one list row - avoid reloading every thumbnail from IndexedDB
        setCharacterListItems((prev) =>
          prev.map((item) =>
            item.id === characterId ? { ...item, lastOpenedAt } : item
          )
        );

        if (character.data.extensions?.[IMPORTED_CHARACTER_FLAG] === true) {
          await characterSnapshotService.createSnapshot(character, 'open').catch(error => {
            console.error('Failed to create baseline snapshot:', error);
          });
        }
        setCurrentCharacterId(characterId);

        // Update last active character in settings
        if (settings) {
          await characterDb.updateSettings({ lastActiveCharacterId: characterId });
          setSettings({ ...settings, lastActiveCharacterId: characterId });
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to open character'));
    }
  }, [settings]);

  /**
   * Close the current character
   */
  const closeCharacter = useCallback(() => {
    dropCharacterPayload();
  }, [dropCharacterPayload]);

  /**
   * Delete a character
   */
  const deleteCharacter = useCallback(async (characterId: string): Promise<void> => {
    try {
      discardPendingSavesForCharacter(characterId);
      for (const key of [...specUpdateSequenceRef.current.keys()]) {
        if (key.startsWith(`${characterId}:`)) specUpdateSequenceRef.current.delete(key);
      }
      await flushChatSessions().catch(() => undefined);
      await pendingChatWritesForOwner('character', characterId);
      await characterDb.deleteCharacter(characterId);
      dropPendingChatWritesForOwner('character', characterId);
      setCharacters((prev) => prev.filter((c) => c.id !== characterId));
      setCharacterListItems((prev) => prev.filter((c) => c.id !== characterId));

      if (currentCharacterId === characterId) {
        setCurrentCharacterId(null);
        setCharacters([]);
      }
      if (settingsRef.current?.lastActiveCharacterId === characterId) {
        setSettings({ ...settingsRef.current, lastActiveCharacterId: undefined });
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to delete character'));
      throw err;
    }
  }, [currentCharacterId]);

  /**
   * Update a character
   */
  const updateCharacter = useCallback(async (
    characterId: string,
    input: UpdateCharacterInput
  ): Promise<Character> => {
    try {
      const updated = await characterDb.updateCharacter(characterId, input);
      setCharacters((prev) =>
        prev.map((c) => (c.id === characterId ? updated : c))
      );
      setCharacterListItems((prev) =>
        prev.map((c) => (c.id === characterId ? toCharacterListItem(updated) : c))
      );
      return updated;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to update character'));
      throw err;
    }
  }, []);

  /**
   * Update a specific spec field
   */
  const updateSpecField = useCallback(async (
    characterId: string,
    field: keyof Character['data']['spec'],
    value: string | string[]
  ): Promise<Character> => {
    const sequenceKey = `${characterId}:${String(field)}`;
    const nextSequence = (specUpdateSequenceRef.current.get(sequenceKey) ?? 0) + 1;
    specUpdateSequenceRef.current.set(sequenceKey, nextSequence);

    try {
      const updated = await characterDb.updateSpecField(characterId, field, value);
      if (specUpdateSequenceRef.current.get(sequenceKey) !== nextSequence) {
        return updated;
      }

      setCharacters((prev) =>
        prev.map((c) => (c.id === characterId ? updated : c))
      );
      setCharacterListItems((prev) =>
        prev.map((c) => (c.id === characterId ? toCharacterListItem(updated) : c))
      );
      return updated;
    } catch (err) {
      if (specUpdateSequenceRef.current.get(sequenceKey) !== nextSequence) {
        throw err;
      }
      setError(err instanceof Error ? err : new Error('Failed to update spec field'));
      throw err;
    }
  }, []);

  /**
   * Duplicate a character
   */
  const duplicateCharacter = useCallback(async (
    characterId: string,
    newName: string
  ): Promise<Character> => {
    try {
      const duplicated = await characterDb.duplicateCharacter(characterId, newName);
      // List only — do not pin the full duplicate in memory until the user opens it
      setCharacterListItems((prev) => [toCharacterListItem(duplicated), ...prev]);
      return duplicated;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to duplicate character'));
      throw err;
    }
  }, []);

  /**
   * Update settings
   */
  const updateSettings = useCallback(async (
    updates: Partial<Omit<CharacterVaultSettings, 'id'>>
  ): Promise<void> => {
    try {
      const updated = await characterDb.updateSettings(updates);
      setSettings(updated);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to update settings'));
      throw err;
    }
  }, []);

  const refreshSettings = useCallback(async (): Promise<void> => {
    try {
      const prefs = await characterDb.getSettings();
      setSettings(prefs);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to refresh settings'));
      throw err;
    }
  }, []);

  const result: CharacterResult = {
    characters,
    characterListItems,
    currentCharacter,
    isLoading,
    error,
    settings,
  };

  const operations: CharacterOperations = {
    createCharacter,
    openCharacter,
    closeCharacter,
    deleteCharacter,
    updateCharacter,
    duplicateCharacter,
    refreshCharacters,
    updateSpecField,
    updateSettings,
    refreshSettings,
  };

  return [result, operations];
}
